import { isDefined } from "@clipboard-health/util-ts";
import { Pill, type PillSize } from "@src/appV2/redesign/system/ui-components";
import { differenceInDays } from "date-fns";
import pluralize from "pluralize";

interface ShiftDocumentsPillProps {
  count: number;
  isExpiringSoon: boolean;
  expiryDate?: Date;
  /**
   * If true, the pill will be compact and show a shorter label
   */
  compact?: boolean;
  size?: PillSize;
}

export function ShiftDocumentsPill(props: ShiftDocumentsPillProps) {
  const { count, isExpiringSoon, expiryDate, compact = false, size = "small" } = props;

  function getLabel() {
    const countLabel = count < 3 ? String(count) : "2+";

    if (compact) {
      return countLabel;
    }

    if (isExpiringSoon) {
      if (count === 1 && isDefined(expiryDate)) {
        const daysDifference = differenceInDays(expiryDate, new Date());
        const expiryText = daysDifference === 0 ? "today" : `in ${daysDifference}d`;

        return `1 document is expiring ${expiryText}`;
      }

      const documentCountText = pluralize("document", count);
      return `${countLabel} ${documentCountText} expiring soon`;
    }

    const documentCountText = pluralize("document", count);
    return `${countLabel} ${documentCountText} needed`;
  }

  const intent = isExpiringSoon ? "warning" : "neutral";

  return (
    <Pill
      size={size}
      label={getLabel()}
      backgroundColor={(theme) => theme.palette.intent?.[intent].background}
      color={(theme) => theme.palette.intent?.[intent].text}
      iconType={compact ? "document" : undefined}
    />
  );
}
